/**
 * Widerrufsfunktion nach § 356a BGB (#272, seit 16.10.2026).
 *
 * Steht im Konto, solange eine Bestellung als Verbraucher widerrufen werden
 * kann (14 Tage, `widerrufbar` aus billing/status). Knopf „Vertrag
 * widerrufen“, dann Name, Vertrag und Adresse für die Bestätigung, dann
 * „Widerruf bestätigen“. Die Eingangsbestätigung mit Inhalt, Datum und
 * Uhrzeit schickt der Server sofort (widerruf.ts). Kein Rückfrage-Dialog,
 * keine Angebote zum Bleiben.
 */

import { useState, type FormEvent } from 'react';
import { ApiFehler, widerrufe, type Status, type Widerrufbar } from '../api';
import { euro } from '../preise';
import { Abschnitt, Feld, Hinweis, KleinerKnopf, Knopf, Panel } from '../ui';
import { locale, t } from '../sprache';

const tag = (iso: string) => new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso)
  .toLocaleDateString(locale(), { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'Europe/Berlin' });
const zeitpunkt = (iso: string) => {
  const z = new Date(iso).toLocaleString(locale(), {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Berlin',
  });
  return t(`${z} Uhr`, z);
};

export default function Widerruf({ status, email, neuLaden }: { status: Status; email: string; neuLaden: () => void }) {
  const offen = status.widerrufbar ?? [];
  const erledigt = status.widerrufen ?? [];
  const [gewaehlt, setGewaehlt] = useState<Widerrufbar | null>(null);
  const [name, setName] = useState(status.rechnungsdaten?.name ?? '');
  const [an, setAn] = useState(email);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  const [ergebnis, setErgebnis] = useState<{ produkt: string; am: string; mail: string; erstattung: number | null; wertersatz: number | null } | null>(null);

  if (!offen.length && !erledigt.length && !ergebnis) return null;

  async function absenden(e: FormEvent) {
    e.preventDefault();
    if (!gewaehlt) return;
    setLaedt(true);
    setFehler('');
    try {
      const r = await widerrufe({ bestellung: gewaehlt.id, name: name.trim(), email: an.trim() });
      setErgebnis({ produkt: gewaehlt.produkt_name, am: r.eingegangen_am, mail: r.mail, erstattung: r.erstattung_cent, wertersatz: r.wertersatz_cent });
      setGewaehlt(null);
      neuLaden();
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : t('Das hat nicht geklappt. Du kannst auch per E-Mail an run@99takes.com widerrufen.', 'That did not work. You can also withdraw by email to run@99takes.com.'));
    } finally {
      setLaedt(false);
    }
  }

  return (
    <div id="widerruf" className="mt-10 scroll-mt-4">
      <Abschnitt
        titel={t('Vertrag widerrufen', 'Withdraw from contract')}
        unter={t('Als Verbraucher kannst du eine Bestellung 14 Tage lang widerrufen. Genutzte Credits ziehen wir als Wertersatz vom Betrag ab, den Rest erstatten wir.',
          'As a consumer, you can withdraw from an order for 14 days. We deduct used credits from the amount as compensation and refund the rest.')}
      >
        {ergebnis && (
          <Hinweis art="gut">
            {t(<>
              Dein Widerruf von {ergebnis.produkt} ist am {zeitpunkt(ergebnis.am)} eingegangen.
              {ergebnis.erstattung != null && <> Wir erstatten {euro(ergebnis.erstattung)}{ergebnis.wertersatz ? ` (abzüglich ${euro(ergebnis.wertersatz)} Wertersatz)` : ''}.</>}
              {ergebnis.mail === 'verschickt' ? ' Die Eingangsbestätigung ist per Mail unterwegs.' : ' Die Bestätigung per Mail schicken wir nach.'}
            </>, <>
              Your withdrawal from {ergebnis.produkt} was received on {zeitpunkt(ergebnis.am)}.
              {ergebnis.erstattung != null && <> We refund {euro(ergebnis.erstattung)}{ergebnis.wertersatz ? ` (minus ${euro(ergebnis.wertersatz)} compensation)` : ''}.</>}
              {ergebnis.mail === 'verschickt' ? ' The confirmation of receipt is on its way by email.' : ' We will send the confirmation by email later.'}
            </>)}
          </Hinweis>
        )}
        {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}
        <Panel>
          {offen.map(b => (
            <div key={b.id} className="border-t border-linie-zart px-4 py-3.5 first:border-t-0 sm:px-5">
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5">
                <span className="min-w-0">
                  <span className="block text-[0.9375rem] font-semibold text-tinte">{b.produkt_name}{b.art === 'plan' ? ' (Plan)' : ''}</span>
                  <span className="mt-0.5 block text-[0.8125rem] text-text-hell tabular-nums">
                    {t(`Bestellt am ${tag(b.bestellt_am)} · ${euro(b.brutto_cent)} · widerrufbar bis ${tag(b.frist_bis)}`,
                      `Ordered on ${tag(b.bestellt_am)} · ${euro(b.brutto_cent)} · can be withdrawn until ${tag(b.frist_bis)}`)}
                  </span>
                </span>
                {gewaehlt?.id !== b.id && (
                  <KleinerKnopf type="button" onClick={() => { setGewaehlt(b); setFehler(''); setErgebnis(null); }}>{t('Vertrag widerrufen', 'Withdraw from contract')}</KleinerKnopf>
                )}
              </div>
              {gewaehlt?.id === b.id && (
                <form onSubmit={absenden} className="mt-4 max-w-[28rem]">
                  <p className="mt-0 mb-3 text-[0.875rem] leading-relaxed text-text">
                    {t(<>
                      Du widerrufst den Vertrag über <strong className="font-semibold">{b.produkt_name}</strong> vom {tag(b.bestellt_am)}.
                      {b.art === 'plan' && ' Der Plan endet damit sofort.'}
                    </>, <>
                      You are withdrawing from the contract for <strong className="font-semibold">{b.produkt_name}</strong> of {tag(b.bestellt_am)}.
                      {b.art === 'plan' && ' The plan ends immediately.'}
                    </>)}
                  </p>
                  <Feld label={t('Dein Name', 'Your name')} required autoComplete="name" value={name} onChange={e => setName(e.target.value)} />
                  <Feld label={t('Bestätigung an', 'Confirmation to')} type="email" required autoComplete="email" value={an} onChange={e => setAn(e.target.value)} />
                  <div className="flex flex-col-reverse gap-2 sm:flex-row">
                    <button type="button" onClick={() => setGewaehlt(null)} disabled={laedt} className="h-12 cursor-pointer rounded-full border border-linie bg-flaeche px-5 text-[0.875rem] font-semibold text-text hover:border-linie-stark">
                      {t('Zurück', 'Back')}
                    </button>
                    <div className="flex-1"><Knopf type="submit" laedt={laedt}>{t('Widerruf bestätigen', 'Confirm withdrawal')}</Knopf></div>
                  </div>
                </form>
              )}
            </div>
          ))}
          {erledigt.map(b => (
            <div key={b.id} className="border-t border-linie-zart px-4 py-3.5 first:border-t-0 sm:px-5">
              <span className="block text-[0.9375rem] font-semibold text-tinte">{b.produkt_name}: {t('widerrufen', 'withdrawn')}</span>
              <span className="mt-0.5 block text-[0.8125rem] text-text-hell tabular-nums">
                {t('Am', 'On')} {tag(b.widerrufen_am)}
                {b.erstattet_cent != null ? t(` · erstattet ${euro(b.erstattet_cent)}`, ` · refunded ${euro(b.erstattet_cent)}`) : b.in_bearbeitung ? t(' · Erstattung in Bearbeitung', ' · refund in progress') : ''}
                {b.wertersatz_cent ? t(` · Wertersatz ${euro(b.wertersatz_cent)}`, ` · compensation ${euro(b.wertersatz_cent)}`) : ''}
              </span>
            </div>
          ))}
        </Panel>
      </Abschnitt>
    </div>
  );
}
